import { useCallback, useEffect, useState } from "react";
import {
  PeliasResult,
  peliasAutocomplete,
} from "../../utils/APICalls/peliasAPI";
import { CUSTOMER_ANALYTICS_EVENT } from "../../utils/enums";
import { useCustomerAnalytics } from "./useCustomerAnalytics";

interface UsePeliasAutocompleteOptions {
  countrycodes?: string;
  limit?: number;
  debounce?: number;
  focus?: {
    lat: number;
    lng: number;
  };
}

interface UsePeliasAutocompleteReturn {
  ready: boolean;
  value: string;
  suggestions: {
    status: "OK" | "ZERO_RESULTS" | "ERROR";
    data: PeliasResult[];
  };
  setValue: (value: string, shouldFetchData?: boolean) => void;
  clearSuggestions: () => void;
}

export const usePeliasAutocomplete = (
  options: UsePeliasAutocompleteOptions = {}
): UsePeliasAutocompleteReturn => {
  const [value, setValueState] = useState("");
  const [suggestions, setSuggestions] = useState<PeliasResult[]>([]);
  const [status, setStatus] = useState<"OK" | "ZERO_RESULTS" | "ERROR">("OK");
  const [ready] = useState(true);
  const [debounceTimer, setDebounceTimer] = useState<NodeJS.Timeout | null>(
    null
  );
  const addCustomerAnalytics = useCustomerAnalytics();

  const searchPlaces = useCallback(
    async (searchValue: string) => {
      if (!searchValue.trim()) {
        setSuggestions([]);
        setStatus("OK");

        return;
      }

      try {
        const searchParams = {
          text: searchValue,
          size: options.limit || 10,
          lang: "en",
          ...(options.focus && {
            focus: {
              point: {
                lat: options.focus.lat,
                lon: options.focus.lng,
              },
            },
          }),

          boundary: {
            country: "EG",
          },
        };

        const results = await peliasAutocomplete(searchParams);

        addCustomerAnalytics(CUSTOMER_ANALYTICS_EVENT.MAP_SEARCH, {
          searchQuery: searchValue,
          searchResult: results.map((result) => result.display_name),
        });

        setSuggestions(results);
        setStatus(results.length > 0 ? "OK" : "ZERO_RESULTS");
      } catch (error) {
        console.error("Pelias search error:", error);
        setSuggestions([]);
        setStatus("ERROR");
      }
    },
    [options.limit, options.focus, addCustomerAnalytics]
  );

  const setValue = useCallback(
    (newValue: string, shouldFetchData = true) => {
      setValueState(newValue);

      if (debounceTimer) {
        clearTimeout(debounceTimer);
      }

      if (shouldFetchData && newValue.trim()) {
        const timer = setTimeout(() => {
          searchPlaces(newValue);
        }, options.debounce || 300);
        setDebounceTimer(timer);
      } else if (!newValue.trim()) {
        setSuggestions([]);
        setStatus("OK");
        setDebounceTimer(null);
      } else {
        setDebounceTimer(null);
      }
    },
    [searchPlaces, options.debounce, debounceTimer]
  );

  const clearSuggestions = useCallback(() => {
    setSuggestions([]);
    setStatus("OK");
    if (debounceTimer) {
      clearTimeout(debounceTimer);
    }
  }, [debounceTimer]);

  useEffect(() => {
    return () => {
      if (debounceTimer) {
        clearTimeout(debounceTimer);
      }
    };
  }, [debounceTimer]);

  return {
    ready,
    value,
    suggestions: { status, data: suggestions },
    setValue,
    clearSuggestions,
  };
};
